import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

const ClientContext = createContext();

const STORAGE_KEY = 'activeClientId';

export const ClientProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [clients, setClients] = useState([]);
  const [activeClientId, setActiveClientIdState] = useState(
    () => localStorage.getItem(STORAGE_KEY) || null
  );

  const isStaff = user && ['super_admin', 'admin', 'store_executive', 'dispatch_executive'].includes(user.role);

  const fetchClients = useCallback(async () => {
    try {
      const res = await api.get('/clients');
      setClients(res.data.data);
    } catch {
      // not authorized (e.g. client_user) — ignore
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && isStaff) fetchClients();
    else setClients([]);
  }, [isAuthenticated, isStaff, fetchClients]);

  const setActiveClient = (id) => {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
    setActiveClientIdState(id);
  };

  const activeClient = clients.find((c) => c._id === activeClientId) || null;

  // Only admin/super_admin can switch clients freely. Staff see all clients in their
  // warehouse; client_user is locked server-side to their own client.
  const effectiveClientId = ['admin', 'super_admin'].includes(user?.role) ? (activeClientId || null) : null;

  return (
    <ClientContext.Provider value={{
      clients,
      activeClient,
      activeClientId: effectiveClientId,
      setActiveClient,
      refetchClients: fetchClients,
    }}>
      {children}
    </ClientContext.Provider>
  );
};

export const useClient = () => useContext(ClientContext);
